import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from "react-toastify";
import { REHYDRATE, persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";
import { backend_api } from "../../../static/server";
import { appendTeamMemberLawyerId } from "../../../styles/styles";

export const getAllUsers = createAsyncThunk(
  "users/getAllUsers",
  async ({ token, page, pageSize, isActive = "True", teamMemberLawyerId }) => {
    try {
      let paginatedUrl = `${backend_api}team/create-employee/?is_active=${isActive}`;
      paginatedUrl = appendTeamMemberLawyerId(paginatedUrl, teamMemberLawyerId);
      const params = [];
      if (page !== undefined) {
        params.push(`page=${page}`);
      }
      if (pageSize !== undefined) {
        params.push(`page_size=${pageSize}`);
      }
      if (params.length > 0) {
        paginatedUrl = `${paginatedUrl}&${params.join("&")}`;
      }

      const response = await axios.get(paginatedUrl, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });

      return {
        ...response.data,
        pagination: {
          page: page || 1,
          pageSize: pageSize || 10,
          rowCount: response.data.count || 0,
          nextPage: response.data.next,
          previousPage: response.data.previous,
        },
      };
    } catch (error) {
      // toast.error("Failed to get users data");
      throw error.response?.data || error.message;
    }
  }
);

export const getAllRoles = createAsyncThunk(
  "users/getAllRoles",
  async ({ token, page, pageSize }) => {
    try {
      let paginatedUrl = `${backend_api}lawer/user-access/`;
      const params = [];
      if (page !== undefined) {
        params.push(`page=${page}`);
      }
      if (pageSize !== undefined) {
        params.push(`page_size=${pageSize}`);
      }
      if (params.length > 0) {
        const separator = paginatedUrl.includes("?") ? "&" : "?";
        paginatedUrl = `${paginatedUrl}${separator}${params.join("&")}`;
      }

      const response = await axios.get(paginatedUrl, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });

      return {
        ...response.data,
        pagination: {
          page: page || 1,
          pageSize: pageSize || 10,
          rowCount: response.data.count || 0,
          nextPage: response.data.next,
          previousPage: response.data.previous,
        },
      };
    } catch (error) {
      toast.error("Failed to get roles data");
      throw error.response?.data || error.message;
    }
  }
);

export const getMySubscription = createAsyncThunk(
  "users/getMySubscription",
  async (
    { token, teamMemberLawyerId } = {},
    { rejectWithValue, getState }
  ) => {
    try {
      const state = getState();
      const resolvedToken = token ?? state?.user?.token;
      const resolvedLawyerId =
        teamMemberLawyerId ?? state?.user?.teamMemberLawyerId ?? null;

      if (!resolvedToken) {
        return rejectWithValue({ message: "Authentication token missing" });
      }

      const endpoint = resolvedLawyerId
        ? appendTeamMemberLawyerId(
            `${backend_api}team/team-subscription`,
            resolvedLawyerId
          )
        : `${backend_api}my-subscription/`;

      const response = await axios.get(endpoint, {
        headers: {
          Authorization: `Token ${resolvedToken}`,
        },
      });
      const payload = response?.data?.data ?? response?.data ?? null;
      return payload;
    } catch (error) {
      if (error?.response?.status === 404) {
        return rejectWithValue(error.response.data);
      }

      if (error?.response?.status === 402) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    condition: ({ force = false } = {}, { getState }) => {
      if (force) {
        return true;
      }

      const state = getState();
      return !state?.user?.subscriptionLoading;
    },
  }
);

export const getMyUsage = createAsyncThunk(
  "users/getMyUsage",
  async ({ token, teamMemberLawyerId } = {}, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const resolvedToken = token ?? state?.user?.token;
      const resolvedLawyerId =
        teamMemberLawyerId ?? state?.user?.teamMemberLawyerId ?? null;

      if (!resolvedToken) {
        return rejectWithValue({ message: "Authentication token missing" });
      }

      const endpoint = appendTeamMemberLawyerId(
        `${backend_api}my-usage/`,
        resolvedLawyerId
      );

      const response = await axios.get(endpoint, {
        headers: { Authorization: `Token ${resolvedToken}` },
      });
      // console.log(response);
      return response.data;
    } catch (error) {
      // toast.error("Failed to get usage data");
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateUserProfile = createAsyncThunk(
  "users/updateUserProfile",
  async ({ token, lawerId, payload }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const resolvedToken = token ?? state?.user?.token;
      const resolvedLawerId = lawerId ?? state?.user?.lawerId;

      if (!resolvedToken || !resolvedLawerId) {
        return rejectWithValue({ message: "Missing profile credentials." });
      }

      const response = await axios.put(
        `${backend_api}lawer/update-profile-lawer/${resolvedLawerId}/`,
        payload,
        {
          headers: {
            Authorization: `Token ${resolvedToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      return response?.data?.data ?? response?.data ?? null;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const extractErrorInfo = (error) => {
  if (!error) {
    return { code: null, message: null };
  }

  if (typeof error === "string") {
    return { code: null, message: error };
  }

  if (Array.isArray(error)) {
    return extractErrorInfo(error[0]);
  }

  if (typeof error !== "object") {
    return { code: null, message: String(error) };
  }

  const codeCandidates = [
    error.code,
    error.error_code,
    error.errorCode,
    error.status,
    error.status_code,
    error?.detail?.code,
  ];

  const messageCandidates = [
    error.message,
    error.detail,
    error.error,
    error.description,
    Array.isArray(error.non_field_errors) ? error.non_field_errors[0] : null,
  ];

  const code = codeCandidates.find(
    (candidate) => typeof candidate === "string" && candidate.trim() !== ""
  );
  const message = messageCandidates.find(
    (candidate) => typeof candidate === "string" && candidate.trim() !== ""
  );

  return { code: code || null, message: message || null };
};

const extractLimits = (plan) => {
  // console.log("plan", plan);
  if (!plan) return {};

  const limits = {};
  for (const [key, value] of Object.entries(plan)) {
    if (
      key.endsWith("_limit") ||
      key.endsWith("_limits") ||
      key.endsWith("_count") ||
      key.endsWith("_mb") ||
      key.endsWith("_template_limit") ||
      key.endsWith("_quill_limit") ||
      key.endsWith("_download_limit") ||
      key.endsWith("_fields_count") ||
      key.endsWith("_limit_contact")
    ) {
      limits[key] = value;
    }
  }
  return limits;
};

const looksLikeSubscriptionObject = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }

  const subscriptionHints = [
    "status",
    "subscription_status",
    "current_status",
    "billing_cycle",
    "expires_on",
    "expiry_on",
    "expiry_date",
    "expiration_date",
    "current_period_end",
    "trial_ends_at",
    "is_active",
    "isActive",
    "plan",
    "plan_id",
  ];

  return subscriptionHints.some((key) => key in value);
};

const looksLikePlanObject = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }

  const planHints = [
    "features",
    "plan_id",
    "planId",
    "price",
    "amount",
    "access_level",
    "name",
  ];

  return planHints.some((key) => key in value);
};

const findDeepMatch = (
  value,
  predicate,
  depth = 0,
  visited = new WeakSet()
) => {
  if (depth > 6 || value === null || value === undefined) return null;

  if (Array.isArray(value)) {
    for (const item of value) {
      const match = findDeepMatch(item, predicate, depth + 1, visited);
      if (match) return match;
    }
    return null;
  }

  if (typeof value === "object") {
    if (visited.has(value)) return null;
    visited.add(value);

    if (predicate(value)) {
      return value;
    }

    for (const key of Object.keys(value)) {
      const match = findDeepMatch(value[key], predicate, depth + 1, visited);
      if (match) return match;
    }
  }

  return null;
};

export const normalizeSubscriptionPayload = (payload) => {
  if (!payload) {
    return { subscription: null, plan: null };
  }

  const subscriptionCandidate = findDeepMatch(
    payload,
    looksLikeSubscriptionObject
  );

  const planSource =
    (subscriptionCandidate &&
      (subscriptionCandidate.plan ||
        subscriptionCandidate.plan_details ||
        subscriptionCandidate.subscription_plan ||
        subscriptionCandidate.current_plan)) ||
    payload?.plan ||
    payload?.data?.plan;

  const planCandidate = findDeepMatch(
    planSource || payload,
    looksLikePlanObject
  );

  const subscription = subscriptionCandidate
    ? {
        ...subscriptionCandidate,
        plan:
          planCandidate && typeof planCandidate === "object"
            ? planCandidate
            : subscriptionCandidate.plan ||
              subscriptionCandidate.plan_details ||
              subscriptionCandidate.subscription_plan ||
              subscriptionCandidate.current_plan ||
              null,
      }
    : null;

  const plan =
    (subscription && subscription.plan && typeof subscription.plan === "object"
      ? subscription.plan
      : null) ||
    planCandidate ||
    null;

  return { subscription, plan };
};

const resolveSubscriptionState = (payload) => {
  if (!payload) {
    return { subscription: null, plan: null };
  }

  const normalized = normalizeSubscriptionPayload(payload);
  let { subscription, plan } = normalized;

  if (!subscription && looksLikeSubscriptionObject(payload)) {
    subscription = payload;
  }

  if (!plan) {
    if (subscription) {
      plan =
        subscription.plan ||
        subscription.plan_details ||
        subscription.subscription_plan ||
        subscription.current_plan ||
        null;
    }

    if (!plan && looksLikePlanObject(payload)) {
      plan = payload;
    }

    if (!plan && payload?.plan && looksLikePlanObject(payload.plan)) {
      plan = payload.plan;
    }
  }

  return { subscription: subscription || null, plan: plan || null };
};

const applySubscriptionState = (state, payload) => {
  const { subscription, plan } = resolveSubscriptionState(payload);
  state.mySubscription = subscription;
  state.plan = plan;
  state.userPlanAccessLimit = extractLimits(plan);
};

const calculateRemainingLimits = (plan, usage) => {
  const limits = extractLimits(plan);
  if (!usage) return limits;

  const remaining = {};

  for (const [key, total] of Object.entries(limits)) {
    // look for a matching usage field (e.g., current_case_limit for case_limit)
    const usageKey = `current_${key}`;
    const used = usage[usageKey] ?? 0;
    remaining[key] = Math.max((total ?? 0) - used, 0);
  }

  return remaining;
};

const extractUserCurrencies = (payload) => {
  if (!payload) return [];

  const direct = payload?.extra?.user_currencies;
  if (Array.isArray(direct)) return direct;

  const nested =
    payload?.lawyer?.extra?.user_currencies ||
    payload?.profile?.extra?.user_currencies ||
    payload?.user_data?.extra?.user_currencies ||
    null;

  return Array.isArray(nested) ? nested : [];
};

const extractDefaultCurrency = (payload) => {
  if (!payload) return null;

  const direct =
    payload?.currency || payload?.default_currency || payload?.defaultCurrency;
  if (typeof direct === "string" && direct.trim() !== "") {
    return direct;
  }

  const nested =
    payload?.lawyer?.currency ||
    payload?.profile?.currency ||
    payload?.user_data?.currency ||
    payload?.extra?.currency ||
    payload?.extra?.default_currency ||
    null;

  if (typeof nested === "string" && nested.trim() !== "") {
    return nested;
  }

  const currencies = extractUserCurrencies(payload);
  if (Array.isArray(currencies)) {
    const defaultCurrency = currencies.find((currency) => currency.isDefault);
    if (defaultCurrency?.shortForm) {
      return defaultCurrency.shortForm;
    }
  }

  return null;
};

const extractUserBillLayout = (payload) => {
  if (!payload) return null;

  const direct = payload?.extra?.user_bill_layout;
  if (typeof direct === "string" && direct.trim() !== "") return direct;

  const nested =
    payload?.lawyer?.extra?.user_bill_layout ||
    payload?.profile?.extra?.user_bill_layout ||
    payload?.user_data?.extra?.user_bill_layout ||
    null;

  return typeof nested === "string" && nested.trim() !== "" ? nested : null;
};

const syncDerivedUserState = (state, payload) => {
  const resolvedCurrencies = extractUserCurrencies(payload) || [];
  const resolvedDefaultCurrency = extractDefaultCurrency(payload) || "USD";
  const resolvedUserBillLayout = extractUserBillLayout(payload) || "classic";

  state.UserCurrencyList = resolvedCurrencies;
  state.defaultCurrency = resolvedDefaultCurrency;
  state.userBillLayout = resolvedUserBillLayout;
};

export const USER_PERSIST_WHITELIST = [
  "defaultCurrency",
  "lawerId",
  "teamData",
  "teamMemberLawyerId",
  "token",
  "user",
  "userAccess",
  "userBillLayout",
  "UserCurrencyList",
  "userId",
];

const userSlice = createSlice({
  name: "user",
  initialState: {
    // Users data
    defaultCurrency: "USD",
    userBillLayout: "classic",
    UserCurrencyList: [],
    allUsers: null,
    usersLoading: false,
    usersError: null,
    userPagination: {
      page: 1,
      pageSize: 10,
      rowCount: 0,
      nextPage: null,
      previousPage: null,
    },

    teamData: null,
    teamMemberLawyerId: null,
    // Roles data
    allRoles: null,
    rolesLoading: false,
    rolesError: null,
    rolesPagination: {
      page: 1,
      pageSize: 10,
      rowCount: 0,
      nextPage: null,
      previousPage: null,
    },

    //user Subscription usage
    myUsage: null,
    usageLoading: false,
    usageError: null,

    // User auth and access
    token: null,
    user: null,
    lawerId: null,
    userId: null,
    userAccess: null,
    userPlanAccessLimit: null,
    plan: null,
    mySubscription: null,
    subscriptionLoading: false,
    subscriptionError: null,
    subscriptionStatusCode: null,
    subscriptionStatusMessage: null,
    subscriptionFetched: false,

    profileUpdating: false,
    profileUpdateError: null,
  },
  reducers: {
    setUser: (state, action) => {
      state.user = action.payload;
      syncDerivedUserState(state, action.payload);
    },
    setTeamData: (state, action) => {
      state.teamData = action.payload;
    },
    setTeamMemberLawyerId: (state, action) => {
      state.teamMemberLawyerId = action.payload;
    },
    setToken: (state, action) => {
      state.token = action.payload;
      state.subscriptionFetched = false;
    },
    setUserAccess: (state, action) => {
      state.userAccess = action.payload;
    },
    setPlan: (state, action) => {
      state.plan = action.payload;
      state.userPlanAccessLimit = extractLimits(action.payload);
    },
    setLawerId: (state, action) => {
      state.lawerId = action.payload;
    },
    setUserId: (state, action) => {
      state.userId = action.payload;
    },
    setUserPagination: (state, action) => {
      state.userPagination = { ...state.userPagination, ...action.payload };
    },
    setRolesPagination: (state, action) => {
      state.rolesPagination = { ...state.rolesPagination, ...action.payload };
    },
    setSubscriptionState: (state, action) => {
      applySubscriptionState(state, action.payload);
    },
    setUserCurrencyList: (state, action) => {
      state.UserCurrencyList = action.payload;
      if (Array.isArray(action.payload)) {
        const defaultCurrency = action.payload.find(
          (currency) => currency.isDefault
        );
        if (defaultCurrency?.shortForm) {
          state.defaultCurrency = defaultCurrency.shortForm;
        }
      }
    },

    logout: (state) => {
      state.token = null;
      state.teamData = null;
      state.user = null;
      state.lawerId = null;
      state.userId = null;
      state.allUsers = null;
      state.allRoles = null;
      state.userAccess = null;
      state.mySubscription = null;
      state.userPlanAccessLimit = {};
      state.plan = null;
      state.subscriptionError = null;
      state.subscriptionStatusCode = null;
      state.subscriptionStatusMessage = null;
      state.subscriptionFetched = false;
      state.UserCurrencyList = [];
      state.defaultCurrency = "USD";
      state.userBillLayout = "classic";
      state.profileUpdating = false;
      state.profileUpdateError = null;
      state.userPagination = {
        page: 1,
        pageSize: 10,
        rowCount: 0,
        nextPage: null,
        previousPage: null,
      };
      state.rolesPagination = {
        page: 1,
        pageSize: 10,
        rowCount: 0,
        nextPage: null,
        previousPage: null,
      };
      localStorage.removeItem("persist:root");
    },
  },
  extraReducers: (builder) => {
    builder
      // Users cases
      .addCase(getAllUsers.pending, (state) => {
        state.usersLoading = true;
        state.usersError = null;
      })
      .addCase(getAllUsers.fulfilled, (state, action) => {
        state.usersLoading = false;
        state.allUsers = action.payload.results;
        state.userPagination = {
          ...state.userPagination,
          page: action.payload.pagination.page,
          pageSize: action.payload.pagination.pageSize,
          rowCount: action.payload.count,
          nextPage: action.payload.next,
          previousPage: action.payload.previous,
        };
      })
      .addCase(getAllUsers.rejected, (state, action) => {
        state.usersLoading = false;
        state.usersError = action.payload;
      })

      // Roles cases
      .addCase(getAllRoles.pending, (state) => {
        state.rolesLoading = true;
        state.rolesError = null;
      })
      .addCase(getAllRoles.fulfilled, (state, action) => {
        state.rolesLoading = false;
        state.allRoles = action.payload.results;
        state.rolesPagination = {
          ...state.rolesPagination,
          page: action.payload.pagination.page,
          pageSize: action.payload.pagination.pageSize,
          rowCount: action.payload.count,
          nextPage: action.payload.next,
          previousPage: action.payload.previous,
        };
      })
      .addCase(getAllRoles.rejected, (state, action) => {
        state.rolesLoading = false;
        state.rolesError = action.payload;
      })
      .addCase(getMySubscription.pending, (state) => {
        state.subscriptionLoading = true;
        state.subscriptionError = null;
        state.subscriptionStatusCode = null;
        state.subscriptionStatusMessage = null;
        state.subscriptionFetched = true;
      })
      .addCase(getMySubscription.fulfilled, (state, action) => {
        state.subscriptionLoading = false;
        state.subscriptionError = null;
        state.subscriptionStatusCode = null;
        state.subscriptionStatusMessage = null;
        state.subscriptionFetched = true;
        if (action.payload) {
          applySubscriptionState(state, action.payload);
        } else {
          state.mySubscription = null;
          state.plan = null;
          state.userPlanAccessLimit = {};
        }
      })
      .addCase(getMySubscription.rejected, (state, action) => {
        state.subscriptionLoading = false;
        state.subscriptionError = action.payload;
        const { code, message } = extractErrorInfo(action.payload);
        state.subscriptionStatusCode = code || null;
        state.subscriptionStatusMessage = message;
        state.subscriptionFetched = true;
        state.mySubscription = null;
        state.plan = null;
        state.userPlanAccessLimit = {};
      })
      .addCase(getMyUsage.pending, (state) => {
        state.usageLoading = true;
        state.usageError = null;
      })
      .addCase(getMyUsage.fulfilled, (state, action) => {
        state.usageLoading = false;
        state.myUsage = action.payload;
        state.userPlanAccessLimit = calculateRemainingLimits(
          state.plan,
          action.payload
        );
      })
      .addCase(getMyUsage.rejected, (state, action) => {
        state.usageLoading = false;
        state.usageError = action.payload;
      })
      .addCase(updateUserProfile.pending, (state) => {
        state.profileUpdating = true;
        state.profileUpdateError = null;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.profileUpdating = false;
        state.profileUpdateError = null;
        if (action.payload) {
          state.user = action.payload;
          syncDerivedUserState(state, action.payload);
        }
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.profileUpdating = false;
        state.profileUpdateError = action.payload;
      })
      .addCase(REHYDRATE, (state, action) => {
        const incomingState = action?.payload?.user;

        if (incomingState?.user) {
          state.user = incomingState.user;
          syncDerivedUserState(state, incomingState.user);
        }
      });
  },
});

export const {
  setUser,
  setTeamData,
  setTeamMemberLawyerId,
  setToken,
  setLawerId,
  setUserId,
  setUserAccess,
  setPlan,
  setUserPagination,
  setRolesPagination,
  setSubscriptionState,
  setUserCurrencyList,
  logout,
} = userSlice.actions;

const persistConfig = {
  key: "root",
  storage,
  whitelist: USER_PERSIST_WHITELIST,
};

export const persistedReducer = persistReducer(
  persistConfig,
  userSlice.reducer
);

export const UserReducer = userSlice.reducer;

// this code will go above of the logout reducer

// rehydrateUserData: (state) => {
//   // Only rehydrate if we have user data but currency list is empty or null
//   if (state.user && (!state.UserCurrencyList || state.UserCurrencyList.length === 0)) {
//     const resolvedCurrencies = extractUserCurrencies(state.user) || [];
//     const resolvedDefaultCurrency = extractDefaultCurrency(state.user) || "USD";
//     const resolvedUserBillLayout = extractUserBillLayout(state.user) || "classic";

//     if (resolvedCurrencies.length > 0) {
//       state.UserCurrencyList = resolvedCurrencies;
//     }
//     if (resolvedDefaultCurrency !== "USD") {
//       state.defaultCurrency = resolvedDefaultCurrency;
//     }
//     if (resolvedUserBillLayout !== "classic") {
//       state.userBillLayout = resolvedUserBillLayout;
//     }
//   }
// },

// use this in app.js when you thing user loose its data again and again in redux
//  const dispatch = useDispatch();

//   useEffect(() => {
//     // This runs ONLY ONCE when the app mounts (after Redux persistence is restored)
//     dispatch(rehydrateUserData());
//   }, [dispatch]);
